import React, { useState } from 'react';
import { useSortableTable } from './hooks/useSortableTable';
import { formatCurrency } from './utils/currency';
import { apiFetch } from './utils/api';

function EmployeePlantTab({ employeeId, plant, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  
  // Sorting
  const { items: sortedPlant, requestSort, getSortIndicator } = useSortableTable(plant, { key: 'allocated_date', direction: 'desc' });
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    item_name: '',
    item_type: 'Tools',
    serial_number: '',
    asset_number: '',
    allocated_date: new Date().toISOString().split('T')[0],
    return_date: '',
    condition: 'Good',
    purchase_value: '',
    notes: ''
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      item_name: '',
      item_type: 'Tools',
      serial_number: '',
      asset_number: '',
      allocated_date: new Date().toISOString().split('T')[0],
      return_date: '',
      condition: 'Good',
      purchase_value: '',
      notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      item_name: item.item_name || '',
      item_type: item.item_type || 'Tools',
      serial_number: item.serial_number || '',
      asset_number: item.asset_number || '',
      allocated_date: item.allocated_date ? item.allocated_date.split('T')[0] : '',
      return_date: item.return_date ? item.return_date.split('T')[0] : '',
      condition: item.condition || 'Good',
      purchase_value: item.purchase_value || '',
      notes: item.notes || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.item_name) {
      alert('Item name is required');
      return;
    }

    setSaving(true);
    try {
      const url = editingItem
        ? `/api/employees/plant/${editingItem.id}`
        : '/api/employees/plant';

      const response = await apiFetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          employee_id: employeeId
        })
      });

      if (response.ok) {
        setShowModal(false);
        onRefresh();
      } else {
        alert('Error saving plant item');
      }
    } catch (error) {
      console.error('Error saving plant:', error);
      alert('Error saving plant item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, itemName) => {
    if (!window.confirm(`Delete "${itemName}"?`)) return;

    try {
      await apiFetch(`/api/employees/plant/${id}`, {
        method: 'DELETE'
      });
      onRefresh();
    } catch (error) {
      console.error('Error deleting plant:', error);
      alert('Error deleting plant item');
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Company Plant & Equipment</h2>
          <button 
            onClick={handleAdd}
            className="bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded transition-colors"
          >
            + Add Item
          </button>
        </div>

      {plant.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No equipment allocated</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-300">
              <tr>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('item_name')}
                >
                  Item Name{getSortIndicator('item_name')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('item_type')}
                >
                  Type{getSortIndicator('item_type')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Serial Number</th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('allocated_date')}
                >
                  Allocated Date{getSortIndicator('allocated_date')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('condition')}
                >
                  Condition{getSortIndicator('condition')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('purchase_value')}
                >
                  Value{getSortIndicator('purchase_value')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedPlant.map((item, idx) => (
                <tr key={item.id} className={idx % 2 === 1 ? 'bg-slate-50' : 'bg-white'}>
                  <td className="px-4 py-3 text-sm text-gray-900">{item.item_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.item_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.serial_number || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(item.allocated_date)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item.condition === 'New' ? 'bg-green-100 text-green-800' :
                      item.condition === 'Good' ? 'bg-blue-100 text-blue-800' :
                      item.condition === 'Fair' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {item.condition}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 font-medium">{formatCurrency(item.purchase_value)}</td>
                  <td className="px-4 py-3 text-sm space-x-2">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id, item.item_name)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>

    {/* Add/Edit Modal */}
    {showModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-slate-600 text-white px-6 py-4 rounded-t-lg">
            <h3 className="text-xl font-semibold">
              {editingItem ? 'Edit Plant Item' : 'Add Plant Item'}
            </h3>
          </div>

          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Item Name *</label>
                <input
                  type="text"
                  value={formData.item_name}
                  onChange={(e) => setFormData({...formData, item_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={formData.item_type}
                  onChange={(e) => setFormData({...formData, item_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                >
                  <option value="Tools">Tools</option>
                  <option value="PPE">PPE</option>
                  <option value="IT Equipment">IT Equipment</option>
                  <option value="Safety Equipment">Safety Equipment</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                <input
                  type="text"
                  value={formData.serial_number}
                  onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Asset Number</label>
                <input
                  type="text"
                  value={formData.asset_number}
                  onChange={(e) => setFormData({...formData, asset_number: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Allocated Date</label>
                <input
                  type="date"
                  value={formData.allocated_date}
                  onChange={(e) => setFormData({...formData, allocated_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Return Date</label>
                <input
                  type="date"
                  value={formData.return_date}
                  onChange={(e) => setFormData({...formData, return_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                <select
                  value={formData.condition}
                  onChange={(e) => setFormData({...formData, condition: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                >
                  <option value="New">New</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Poor">Poor</option>
                  <option value="Damaged">Damaged</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Value</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">£</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.purchase_value}
                    onChange={(e) => setFormData({...formData, purchase_value: e.target.value})}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
              />
            </div>
          </div>

          <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded disabled:opacity-50"
            >
              {saving ? 'Saving...' : (editingItem ? 'Update' : 'Add Item')}
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
}

export default EmployeePlantTab;
